/**
 * Audit Trail Reason Codes Configuration
 *
 * Defines deletion/change reason codes for different modules.
 * Each module can have its own set of applicable reasons.
 *
 * @module config/auditReasons
 */

/**
 * Base reason codes for equipment deletions
 */
export const EQUIPMENT_DELETION_REASONS = [
  {
    code: 'replaced',
    label: 'Equipment replaced/upgraded',
    requiresNotes: false,
    description: 'Item was replaced with newer or upgraded equipment'
  },
  {
    code: 'not_required',
    label: 'No longer required (vessel config changed)',
    requiresNotes: false,
    description: 'Vessel configuration changed and equipment is no longer needed'
  },
  {
    code: 'lost_stolen',
    label: 'Lost or stolen',
    requiresNotes: false,
    description: 'Equipment was lost overboard or stolen'
  },
  {
    code: 'repair_uneconomical',
    label: 'Beyond economical repair',
    requiresNotes: false,
    description: 'Equipment damaged beyond cost-effective repair'
  },
  {
    code: 'end_of_life',
    label: 'End of service life',
    requiresNotes: false,
    description: 'Equipment reached end of rated service life'
  },
  {
    code: 'transferred',
    label: 'Transferred to another vessel',
    requiresNotes: true,
    description: 'Equipment moved to another vessel in the fleet'
  },
  {
    code: 'mistake',
    label: 'Created by mistake',
    requiresNotes: false,
    description: 'Record was created in error'
  },
  {
    code: 'other',
    label: 'Other (explain below)',
    requiresNotes: true,
    description: 'Other reason not listed above'
  }
];

/**
 * Future: Reason codes for crew changes/deletions
 */
export const CREW_DELETION_REASONS = [
  {
    code: 'discharged',
    label: 'Crew member discharged',
    requiresNotes: false
  },
  {
    code: 'transferred',
    label: 'Transferred to another vessel',
    requiresNotes: true
  },
  {
    code: 'mistake',
    label: 'Created by mistake',
    requiresNotes: false
  },
  {
    code: 'other',
    label: 'Other (explain below)',
    requiresNotes: true
  }
];

/**
 * Reason codes for risk assessment deletions
 */
export const RISK_ASSESSMENT_DELETION_REASONS = [
  {
    code: 'superseded',
    label: 'Superseded by new assessment',
    requiresNotes: false,
    description: 'This assessment has been replaced by a newer version'
  },
  {
    code: 'no_longer_applicable',
    label: 'Task/operation no longer performed',
    requiresNotes: false,
    description: 'The operation this assessment covers is no longer carried out'
  },
  {
    code: 'duplicate',
    label: 'Duplicate entry',
    requiresNotes: false,
    description: 'This is a duplicate of another risk assessment'
  },
  {
    code: 'created_in_error',
    label: 'Created in error',
    requiresNotes: false,
    description: 'This assessment was created by mistake'
  },
  {
    code: 'vessel_change',
    label: 'Vessel/equipment changed',
    requiresNotes: true,
    description: 'The vessel or equipment configuration has changed'
  },
  {
    code: 'regulatory_change',
    label: 'Regulatory requirements changed',
    requiresNotes: true,
    description: 'Regulatory requirements have changed requiring new assessment'
  },
  {
    code: 'other',
    label: 'Other (explain below)',
    requiresNotes: true,
    description: 'Other reason not listed above'
  }
];

/**
 * Future: Reason codes for defect deletions
 */
export const DEFECT_DELETION_REASONS = [
  {
    code: 'duplicate',
    label: 'Duplicate entry',
    requiresNotes: false
  },
  {
    code: 'mistake',
    label: 'Created by mistake',
    requiresNotes: false
  },
  {
    code: 'not_defect',
    label: 'Not actually a defect',
    requiresNotes: true
  },
  {
    code: 'other',
    label: 'Other (explain below)',
    requiresNotes: true
  }
];

/**
 * Get deletion reasons for a specific module
 * @param {string} module - Module name (e.g., 'equipment', 'crew', 'defects')
 * @returns {Array} Array of reason objects
 */
export const getDeletionReasons = (module) => {
  const reasonMap = {
    equipment: EQUIPMENT_DELETION_REASONS,
    crew: CREW_DELETION_REASONS,
    defects: DEFECT_DELETION_REASONS,
    'risk-assessment': RISK_ASSESSMENT_DELETION_REASONS
  };

  return reasonMap[module] || EQUIPMENT_DELETION_REASONS;
};

/**
 * Get reason label by code
 * @param {string} module - Module name
 * @param {string} code - Reason code
 * @returns {string} Reason label or code if not found
 */
export const getReasonLabel = (module, code) => {
  const reasons = getDeletionReasons(module);
  const reason = reasons.find(r => r.code === code);
  return reason ? reason.label : code;
};
